import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LabelsDropdown } from "@/components/popups/labels-dropdown";
import { cn } from "@/lib/utils";
import { Filter, X } from "lucide-react";
import { useState } from "react";

interface FilterOption {
  value: string;
  label: string;
  bgcolor?: string;
}

interface ConsolidatedFilterDropdownProps {
  // Labels filter
  labels: FilterOption[];
  selectedLabels: string[];
  onLabelsChange: (labels: string[]) => void;
  
  // ESP Types filter (for mailboxes)
  espTypes?: FilterOption[];
  selectedEspTypes?: string[];
  onEspTypesChange?: (espTypes: string[]) => void;
  
  // Warmup Status filter (for mailboxes)
  warmupStatusOptions?: FilterOption[];
  selectedWarmupStatus?: boolean | undefined;
  onWarmupStatusChange?: (status: string[]) => void;
  
  // Seed Assigned filter (for sender profiles)
  seedAssignedOptions?: FilterOption[];
  selectedSeedAssigned?: string[];
  onSeedAssignedChange?: (seeds: string[]) => void;
  
  // Sending Volume filter (for sender profiles)
  sendingVolumeOptions?: FilterOption[];
  selectedSendingVolume?: string[];
  onSendingVolumeChange?: (volumes: string[]) => void;
  
  // Connection Status filter
  connectionStatusOptions?: FilterOption[];
  selectedConnectionStatus?: boolean | undefined;
  onConnectionStatusChange?: (status: string[]) => void;
  
  // Subscription filter
  subscriptionOptions: FilterOption[];
  selectedSubscription: string[];
  onSubscriptionChange: (subscription: string[]) => void;
  
  className?: string;
}

export function ConsolidatedFilterDropdown({
  labels,
  selectedLabels,
  onLabelsChange,
  espTypes,
  selectedEspTypes,
  onEspTypesChange,
  warmupStatusOptions,
  selectedWarmupStatus,
  onWarmupStatusChange,
  seedAssignedOptions,
  selectedSeedAssigned,
  onSeedAssignedChange,
  sendingVolumeOptions,
  selectedSendingVolume,
  onSendingVolumeChange,
  connectionStatusOptions,
  selectedConnectionStatus,
  onConnectionStatusChange,
  subscriptionOptions,
  selectedSubscription,
  onSubscriptionChange,
  className,
}: ConsolidatedFilterDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  
  // Calculate total active filters
  const activeFiltersCount = 
    selectedLabels.length +
    (selectedEspTypes?.length || 0) +
    (selectedWarmupStatus !== undefined ? 1 : 0) +
    (selectedSeedAssigned?.length || 0) +
    (selectedSendingVolume?.length || 0) +
    (selectedConnectionStatus !== undefined ? 1 : 0) +
    selectedSubscription.length;

  const clearAllFilters = () => {
    onLabelsChange([]);
    onEspTypesChange?.([]);
    onWarmupStatusChange?.([]);
    onSeedAssignedChange?.([]);
    onSendingVolumeChange?.([]);
    // onConnectionStatusChange([]);
    onSubscriptionChange([]);
  };

  return (
    <div className={cn("relative", className)}>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="outline" 
            className="h-9 px-3 border-border hover:bg-muted/50"
            onClick={() => setIsOpen(!isOpen)}
          >
            <Filter className="h-4 w-4" />
            <span className="hidden xs:block ml-2">Filters</span>
            {activeFiltersCount > 0 && (
              <span className="ml-1 bg-primary text-primary-foreground text-xs rounded-full px-1.5 py-0.5 min-w-[20px] text-center font-medium">
                {activeFiltersCount}
              </span>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent 
          align="end" 
          className="w-60 max-h-[500px] overflow-y-auto bg-background border-border"
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <DropdownMenuLabel className="flex items-center justify-between px-2 py-1.5">
            <span>Filter Options</span>
            {activeFiltersCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAllFilters}
                className="h-5 px-1.5 text-xs text-muted-foreground hover:text-foreground"
              >
                <X className="h-3 w-3 mr-1" />
                Clear All
              </Button>
            )}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          
          {/* Labels Filter */}
          <div className="px-2 py-1">
            <LabelsDropdown
              onLabelsChange={onLabelsChange}
              multiSelect={true}
              placeholder="Filter by labels"
              labels={labels}
              value={selectedLabels}
              align="start"
              className="w-full"
            />
          </div>
          
          {/* ESP Types Filter (for mailboxes) */}
          {espTypes && (
            <div className="px-2 py-1">
              <LabelsDropdown
                onLabelsChange={onEspTypesChange || (() => {})}
                multiSelect={true}
                placeholder="Filter by ESP types"
                searchPlaceholder="Select ESP types..."
                labels={espTypes}
                value={selectedEspTypes || []}
                align="start"
                className="w-full"
              />
            </div>
          )}
          
          {/* Warmup Status Filter (for mailboxes) */}
          {warmupStatusOptions && (
            <div className="px-2 py-1">
              <LabelsDropdown
                onLabelsChange={onWarmupStatusChange || (() => {})}
                multiSelect={false}
                placeholder="Filter by warmup status"
                searchPlaceholder="Select warmup status..."
                labels={warmupStatusOptions}
                value={
                  selectedWarmupStatus === true
                    ? ["true"]
                    : selectedWarmupStatus === false
                      ? ["false"]
                      : ([] as string[])
                }
                align="start"
                className="w-full"
              />
            </div>
          )}
          
          {/* Seed Assigned Filter (for sender profiles) */}
          {seedAssignedOptions && (
            <div className="px-2 py-1">
              <LabelsDropdown
                onLabelsChange={onSeedAssignedChange || (() => {})}
                multiSelect={true}
              placeholder="Filter by seed assigned"
                searchPlaceholder="Select seed assigned..."
                labels={seedAssignedOptions}
                value={selectedSeedAssigned || []}
                align="start"
                className="w-full"
              />
            </div>
          )}
          
          {/* Sending Volume Filter (for sender profiles) */}
          {sendingVolumeOptions && (
            <div className="px-2 py-1">
              <LabelsDropdown
                onLabelsChange={onSendingVolumeChange || (() => {})}
                multiSelect={true}
                placeholder="Filter by sending volume"
                searchPlaceholder="Select sending volume..."
                labels={sendingVolumeOptions}
                value={selectedSendingVolume || []}
                align="start"
                className="w-full"
              />
            </div>
          )}
          
          {/* Connection Status Filter */}
          {connectionStatusOptions && (
            <div className="px-2 py-1">
              <LabelsDropdown
                onLabelsChange={onConnectionStatusChange || (() => {})}
                multiSelect={false}
                placeholder="Filter by connection status"
                searchPlaceholder="Select connection status..."
                labels={connectionStatusOptions}
                value={
                  selectedConnectionStatus === true
                    ? ["true"]
                    : selectedConnectionStatus === false
                      ? ["false"]
                      : ([] as string[])
                }
                align="start"
                className="w-full"
              />
            </div>
          )}
          
          {/* Subscription Filter */}
          <div className="px-2 py-1">
            <LabelsDropdown
              onLabelsChange={onSubscriptionChange}
              multiSelect={true}
              placeholder="Filter by subscription"
              searchPlaceholder="Select subscription..."
              labels={subscriptionOptions}
              value={selectedSubscription}
              align="start"
              className="w-full"
            />
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
